import { StringUtils } from '../../../utils/string-utils';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { Product } from './product';
import { Variant } from './variant';
import { LocationPriceStream } from '../../enum/shared/location-price-stream';
import { Menu } from '../../menu/menu';
import { Section } from '../../menu/section/section';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import { Cannabinoid, SecondaryCannabinoid } from '../../enum/shared/cannabinoid';

export class VariantGroup implements UniquelyIdentifiable {
  constructor(
    public product: Product,
    public variants: Variant[]
  ) {}

  private returnVariantInfo(): boolean {
    return this.variants?.length === 1;
  }

  public getMaxVariantStock(): number {
    const quantityInStock = this.variants?.map(v => v?.inventory?.quantityInStock)?.filterNulls() || [];
    return Math.max(...quantityInStock, 0);
  }

  public getGroupTitle(): string {
    return this.returnVariantInfo() ? this.variants?.firstOrNull()?.getVariantTitle() : this.product?.getProductTitle();
  }

  public getGroupBrand(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.brand));
  }

  public getGroupManufacturer(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.manufacturer));
  }

  public getGroupClassification(): StrainType | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.classification));
  }

  public getGroupLineage(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getLineagesAsString()));
  }

  public getGroupProductType(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.productType));
  }

  public getVariantType(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.variantType));
  }

  public sortVariantsBy(
    sortBy: (variants: Variant[], menu: Menu, section: Section, locationPriceStream: LocationPriceStream) => Variant[],
    menu: Menu,
    section: Section,
    locationPriceStream: LocationPriceStream
  ): void {
    if (this.variants?.length > 1) {
      this.variants = sortBy(this.variants, menu, section, locationPriceStream);
    }
  }

  /**
   * Returns the minimum price of the variants in the group.
   * Can return Infinity if prices are not specified.
   */
  public getMinGroupPrice(
    themeId: string,
    locationId: number,
    companyId: number,
    locationPriceStream: LocationPriceStream,
    hideSale: boolean
  ): number {
    const price =
      this.variants
        ?.map(v => v.getVisiblePrice(themeId, locationId, companyId, locationPriceStream, hideSale))
        ?.filterNulls() || [];
    return Math.min(...price);
  }

  /**
   * Returns the minimum secondary price of the variants in the group.
   * Can return Infinity if secondary prices are not specified.
   */
  public getMinGroupSecondaryPrice(
    themeId: string,
    companyId: number,
    locationId: number,
    priceStream: LocationPriceStream
  ): number {
    const secondaryPrice =
      this.variants?.map(v => v.getSecondaryPrice(themeId, companyId, locationId, priceStream))?.filterNulls() || [];
    return Math.min(...secondaryPrice);
  }

  /**
   * Returns the minimum price per unit of measure of the variants in the group.
   * Can return Infinity if group prices per unit of measure are not specified.
   */
  public getMinGroupPricePerUOM(
    tId: string, // themeId
    locId: number,
    compId: number,
    locPriceStream: LocationPriceStream,
    hideSale: boolean
  ): number {
    const pricePerUOM = this.variants?.map(v => v.getPricePerUOM(tId, locId, compId, locPriceStream, hideSale)) || [];
    return Math.min(...pricePerUOM);
  }

  public getMaxGroupPrice(
    themeId: string,
    locId: number,
    compId: number,
    locPriceStream: LocationPriceStream,
    hideSale: boolean
  ): number {
    const price = this.variants?.map(v => v.getVisiblePrice(themeId, locId, compId, locPriceStream, hideSale)) || [];
    return Math.max(...price, 0);
  }

  public getMaxGroupSecondaryPrice(
    themeId: string,
    compId: number,
    locationId: number,
    priceStream: LocationPriceStream
  ): number {
    const secondaryPrice = this.variants?.map(v => v.getSecondaryPrice(themeId, compId, locationId, priceStream)) || [];
    return Math.max(...secondaryPrice, 0);
  }

  public getMaxGroupPricePerUOM(
    tId: string, // themeId
    locId: number,
    compId: number,
    locPriceStream: LocationPriceStream,
    hideSale: boolean
  ): number {
    const pricePerUOM = this.variants?.map(v => v.getPricePerUOM(tId, locId, compId, locPriceStream, hideSale)) || [];
    return Math.max(...pricePerUOM, 0);
  }

  /**
   * Returns the minimum price of the variants in the group without any discounts applied.
   * Can return Infinity if prices are not specified.
   */
  public getMinOriginalPrice(
    tId: string, // themeId
    locId: number,
    compId: number,
    locPriceStream: LocationPriceStream
  ): number {
    const originalPrice = this.variants?.map(v => v.getPriceWithoutDiscounts(tId, locId, compId, locPriceStream)) || [];
    return Math.min(...originalPrice);
  }

  public getMaxOriginalPrice(
    tId: string, // themeId
    locId: number,
    compId: number,
    locPriceStream: LocationPriceStream
  ): number {
    const originalPrice = this.variants?.map(v => v.getPriceWithoutDiscounts(tId, locId, compId, locPriceStream)) || [];
    return Math.max(...originalPrice, 0);
  }

  /**
   * Returns the minimum sale price of the variants in the group.
   * Can return Infinity if sale prices are not specified.
   */
  public getMinSaleOriginalPrice(
    themeId: string,
    locationId: number,
    companyId: number,
    locationPriceStream: LocationPriceStream
  ): number {
    const salePrice =
      this.variants
        ?.map(v => v?.getSaleOriginalPriceOrNull(themeId, locationId, companyId, locationPriceStream))
        ?.filterNulls() || [];
    return Math.min(...salePrice);
  }

  public getMaxSaleOriginalPrice(
    themeId: string,
    locationId: number,
    companyId: number,
    locationPriceStream: LocationPriceStream
  ): number {
    const salePrice =
      this.variants
        ?.map(v => v?.getSaleOriginalPriceOrNull(themeId, locationId, companyId, locationPriceStream))
        ?.filterNulls() || [];
    return Math.max(...salePrice, 0);
  }

  /**
   * Returns the minimum average price of the variants in the group with the taxes applied.
   * Can return Infinity if prices are not specified.
   */
  public getMinTaxesInPrice(themeId: string, locationId: number, companyId: number): number {
    const taxesInPrice = this.variants?.map(v => v.getTaxesInPrice(themeId, locationId, companyId)) || [];
    return Math.min(...taxesInPrice);
  }

  public getMaxTaxesInPrice(themeId: string, locationId: number, companyId: number): number {
    const taxesInPrice = this.variants?.map(v => v.getTaxesInPrice(themeId, locationId, companyId)) || [];
    return Math.max(...taxesInPrice, 0);
  }

  /**
   * Returns the minimum average price of the variants in the group with the taxes applied and rounded.
   * Can return Infinity if prices are not specified.
   */
  public getMinTaxesInRoundedPrice(themeId: string, locationId: number, companyId: number): number {
    const taxesInPrice = this.variants?.map(v => v.getTaxesInRoundedPrice(themeId, locationId, companyId)) || [];
    return Math.min(...taxesInPrice);
  }

  public getMaxTaxesInRoundedPrice(themeId: string, locationId: number, companyId: number): number {
    const taxesInPrice = this.variants?.map(v => v.getTaxesInRoundedPrice(themeId, locationId, companyId)) || [];
    return Math.max(...taxesInPrice, 0);
  }

  /**
   * Returns the minimum pre-tax price of the variants in the group.
   * Can return Infinity if prices are not specified.
   */
  public getMinPreTaxPrice(themeId: string, locationId: number, companyId: number): number {
    const preTaxPrice = this.variants?.map(v => v.getPreTaxPrice(themeId, locationId, companyId)) || [];
    return Math.min(...preTaxPrice);
  }

  public getMaxPreTaxPrice(themeId: string, locationId: number, companyId: number): number {
    const preTaxPrice = this.variants?.map(v => v.getPreTaxPrice(themeId, locationId, companyId)) || [];
    return Math.max(...preTaxPrice, 0);
  }

  public getGroupEffects(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getEffectsAsString()));
  }

  public getGroupAromas(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getAromasAsString()));
  }

  public getGroupFlavorGroup(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.flavorGroup));
  }

  public getGroupFlavors(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getFlavorsAsString()));
  }

  public getGroupHelpsWith(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getHelpsWithAsString()));
  }

  public getGroupAppearances(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getAppearancesAsString()));
  }

  public getGroupGrowingConditions(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getGrowingConditionsAsString()));
  }

  public getMinEnergyScore(): number | null {
    const scores = (this.variants || [])
      .map(v => v?.getEnergyScoreAsNumber())
      .filter((score): score is number => typeof score === 'number' && Number.isFinite(score));
    if (!scores.length) {
      return null;
    }
    return Math.min(...scores);
  }

  public getMaxEnergyScore(): number | null {
    const scores = (this.variants || [])
      .map(v => v?.getEnergyScoreAsNumber())
      .filter((score): score is number => typeof score === 'number' && Number.isFinite(score));
    if (!scores.length) {
      return null;
    }
    return Math.max(...scores);
  }

  public getGroupCraftDesignations(): string | null {
    return StringUtils.getStringModeOrNull(this.variants?.map(v => v?.getCraftDesignationsAsString()));
  }

  /**
   * Returns the minimum numeric cannabinoids of the variants in the group.
   * Can return Infinity if cannabinoids is not specified.
   */
  public getMinNumericCannabinoid(cannabinoid: string): number {
    const cannabinoids =
      this.variants?.map(v => {
        return v.useCannabinoidRange
          ? v.getNumericMinCannabinoidOrTerpene(cannabinoid)
          : v.getNumericCannabinoidOrTerpene(cannabinoid);
      }) || [];
    return Math.min(...cannabinoids);
  }

  public getMaxNumericCannabinoid(cannabinoid: string): number {
    const cannabinoids =
      this.variants?.map(v => {
        return v.useCannabinoidRange
          ? v.getNumericMaxCannabinoidOrTerpene(cannabinoid)
          : v.getNumericCannabinoidOrTerpene(cannabinoid);
      }) || [];
    return Math.min(...cannabinoids);
  }

  /**
   * Returns the minimum numeric Total Active Cannabinoids for the variants in the group.
   * Can return Infinity if TAC is not specified.
   */
  public getMinNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids: SecondaryCannabinoid[]): number {
    const tac =
      this.variants?.map(v => {
        return v.useCannabinoidRange
          ? v.getNumericMinTotalActiveCannabinoids(enabledSecondaryCannabinoids)
          : v.getNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids);
      }) || [];
    return Math.min(...tac);
  }

  public getMaxNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids: SecondaryCannabinoid[]): number {
    const tac =
      this.variants?.map(v => {
        return v.useCannabinoidRange
          ? v.getNumericMaxTotalActiveCannabinoids(enabledSecondaryCannabinoids)
          : v.getNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids);
      }) || [];
    return Math.max(...tac, 0);
  }

  public getVariantGroupTopCannabinoid(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): string {
    return this.variants
      ?.map(v => v?.getTopCannabinoid(enabledSecondaryCannabinoids, ignoredListCannabinoids))
      ?.filterNulls()
      ?.mode();
  }

  /**
   * Returns the minimum numeric terpenes of the variants in the group.
   * Can return Infinity if terpenes is not specified.
   */
  public getMinNumericTerpene(terpeneCamelCased: string): number {
    const terpenes =
      this.variants?.map(v => {
        return v.useTerpeneRange
          ? v.getNumericMinCannabinoidOrTerpene(terpeneCamelCased)
          : v.getNumericCannabinoidOrTerpene(terpeneCamelCased);
      }) || [];
    return Math.min(...terpenes);
  }

  /**
   * Returns the maximum numeric terpenes of the variants in the group.
   * Can return Infinity if terpenes is not specified.
   */
  public getMaxNumericTerpene(terpeneCamelCased: string): number {
    const terpenes =
      this.variants?.map(v => {
        return v.useTerpeneRange
          ? v.getNumericMaxCannabinoidOrTerpene(terpeneCamelCased)
          : v.getNumericCannabinoidOrTerpene(terpeneCamelCased);
      }) || [];
    return Math.min(...terpenes);
  }

  /**
   * Returns the minimum numeric Total Active Terpenes for the variants in the group.
   * Can return Infinity if Total Active Terpenes is not specified.
   */
  public getMinNumericTotalActiveTerpenes(enabledTerpenesPascalCased: string[]): number {
    const totalActiveTerpenes =
      this.variants?.map(v => {
        return v.useTerpeneRange
          ? v.getNumericMinTotalActiveTerpenes(enabledTerpenesPascalCased)
          : v.getNumericTotalActiveTerpenes(enabledTerpenesPascalCased);
      }) || [];
    return Math.min(...totalActiveTerpenes);
  }

  public getMaxNumericTotalActiveTerpenes(enabledTerpenesPascalCased: string[]): number {
    const totalActiveTerpenes =
      this.variants?.map(v => {
        return v.useTerpeneRange
          ? v.getNumericMaxTotalActiveTerpenes(enabledTerpenesPascalCased)
          : v.getNumericTotalActiveTerpenes(enabledTerpenesPascalCased);
      }) || [];
    return Math.max(...totalActiveTerpenes, 0);
  }

  public getVariantGroupTopTerpene(enabledTerpenesPascalCased: string[]): string {
    return this.variants
      ?.map(v => v?.getTopTerpene(enabledTerpenesPascalCased))
      ?.filterNulls()
      ?.mode();
  }

  public getMinPackageQuantity(): number {
    const packageQuantity = this.variants?.map(v => v?.packagedQuantity) || [];
    const min = Math.min(...packageQuantity);
    return Number.isFinite(min) ? min : 0;
  }

  public getMaxPackageQuantity(): number {
    const packageQuantity = this.variants?.map(v => v?.packagedQuantity) || [];
    return Math.max(...packageQuantity, 0);
  }

  /**
   * Returns the minimum unit size of the variants in the group.
   * Can return Infinity if unit sizes are not specified.
   */
  public getMinNumericUnitSize(): number {
    const size = this.variants?.map(v => v?.unitSize) || [];
    return Math.min(...size);
  }

  public getMaxNumericUnitSize(): number {
    const size = this.variants?.map(v => v?.unitSize) || [];
    return Math.max(...size, 0);
  }

  getUniqueIdentifier(): string {
    return `
      -${this.product?.getUniqueIdentifier()}
      -${this.variants
        ?.map(v => v.getUniqueIdentifier())
        ?.sort()
        ?.join('-')}
    `;
  }
}
